import React, { useEffect, useState } from "react";
import { Megaphone, Pencil, Trash2 } from "lucide-react";
import api from '../api/axios';
import DateFilter from "../components/dashboard/DateRangeFilter";
import { useToast } from "../context/ToastContext";

const CampaignPage = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [formData, setFormData] = useState({
        camp_name: "",
        camp_start_date: "",
        camp_end_date: "",
        camp_status: "active"
    });
    const [errors, setErrors] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);

    const [deleteId, setDeleteId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const { success, error } = useToast();

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            setErrors(validate());
        }
    }, [formData]);

    // ===========================
    // FETCH CAMPAIGNS
    // ===========================
    const fetchCampaigns = async () => {
        try {
            const response = await api.get("/campaign/list");
            if (response?.data?.data) {
                setCampaigns(response.data.data);
            }
        } catch (error) {
            console.error("Fetch error:", error);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    // ===========================
    // HANDLE CHANGE
    // ===========================
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleDateChange = (key, e) => {
        setFormData({
            ...formData,
            [key]: e,
        });

    };

    // ===========================
    // VALIDATION
    // ===========================
    const validate = () => {
        let newErrors = {};

        if (!formData.camp_name.trim())
            newErrors.camp_name = "Campaign name is required";

        if (!formData.camp_start_date)
            newErrors.camp_start_date = "Start date is required";

        if (!formData.camp_end_date)
            newErrors.camp_end_date = "End date is required";

        if (!formData.camp_status)
            newErrors.camp_status = "Status is required";

        // ✅ FIXED DATE VALIDATION
        if (formData.camp_start_date && formData.camp_end_date) {
            const start = new Date(formData.camp_start_date);
            const end = new Date(formData.camp_end_date);

            if (end < start) {
                newErrors.camp_end_date = "End date must be after start date";
            }
        }
        return newErrors;
    };

    // ===========================
    // SUBMIT (CREATE / UPDATE)
    // ===========================
    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length !== 0) return;

        try {
            setLoading(true);

            const payload = {
                ...formData,
                camp_start_date: formData.camp_start_date
                    ? new Date(formData.camp_start_date).toISOString()
                    : null,
                camp_end_date: formData.camp_end_date
                    ? new Date(formData.camp_end_date).toISOString()
                    : null,
            };

            if (editingId) {
                await api.put(`/campaign/${editingId}`, payload);
                success("Campaign updated successfully");
            } else {
                await api.post(`/campaign/save`, payload);
                success("Campaign added successfully");
            }

            setFormData({
                camp_name: "",
                camp_start_date: "",
                camp_end_date: "",
                camp_status: "active"
            });

            setEditingId(null);
            fetchCampaigns();
        } catch (err) {
            if (err.response?.status === 409) {
                error(err.response.data.error);
            }
        } finally {
            setLoading(false);
        }
    };

    // ===========================
    // EDIT
    // ===========================
    const handleEdit = (campaign) => {
        console.log(campaign)
        setFormData({
            camp_name: campaign.camp_name,
            camp_start_date: campaign.camp_start_date,
            camp_end_date: campaign.camp_end_date,
            camp_status: campaign.camp_status,
        });
        setEditingId(campaign.camp_id);
    };

    // ===========================
    // DELETE
    // ===========================
    const handleDelete = async () => {
        if (!deleteId) return;

        try {
            setDeleteLoading(true);
            await api.delete(`/campaign/${deleteId}`);
            fetchCampaigns();
            setDeleteId(null);
            success("Campaign deleted successfully");
        } catch (error) {
            console.error("Delete error:", error);
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="p-6">

            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Campaigns</h1>
                    <p className="text-muted-foreground">Manage all your campaigns</p>
                </div>
            </div>

            <div className="mx-auto grid md:grid-cols-2 gap-6 mt-5">

                {/* ================= FORM ================= */}
                <div className="bg-white p-6 rounded-2xl shadow-md">
                    <div className="flex items-center gap-2 mb-6">
                        <Megaphone className="w-6 h-6 text-black" />
                        <h2 className="text-xl font-semibold">
                            {editingId ? "Edit Campaign" : "Create Campaign"}
                        </h2>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">

                        <div>
                            <label className="text-sm font-medium">Campaign Name</label>
                            <input
                                type="text"
                                name="camp_name"
                                placeholder="Enter campaign name"
                                value={formData.camp_name}
                                onChange={handleChange}
                                className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black"
                            />
                            {errors.camp_name && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.camp_name}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="text-sm font-medium">Start Date</label>
                            <DateFilter
                                className="w-full"
                                name="camp_start_date"
                                value={formData.camp_start_date}
                                onDateChange={(date) => handleDateChange('camp_start_date', date)} />
                            {errors.camp_start_date && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.camp_start_date}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="text-sm font-medium">End Date</label>
                            <DateFilter
                                className="w-full"
                                name="camp_end_date"
                                value={formData.camp_end_date}
                                onDateChange={(date) => handleDateChange('camp_end_date', date)} />
                            {errors.camp_end_date && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.camp_end_date}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="text-sm font-medium">Status</label>
                            <select className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-black"
                                name="camp_status"
                                placeholder="Select status"
                                value={formData.camp_status}
                                onChange={handleChange}>
                                <option value={'active'}>Active</option>
                                <option value={'inactive'}>Inactive</option>
                            </select>

                            {errors.camp_status && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.camp_status}
                                </p>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-black hover:bg-gray-900 text-white py-2 rounded-lg 
               flex items-center justify-center gap-2 disabled:opacity-70"
                        >
                            {loading ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    Saving...
                                </>
                            ) : (
                                editingId ? "Update Campaign" : "Create Campaign"
                            )}
                        </button>
                    </form>
                </div>

                {/* ================= LIST ================= */}
                <div className="bg-white p-6 rounded-2xl shadow-md">
                    <h2 className="text-xl font-semibold mb-4">Campaign List</h2>

                    {campaigns.length === 0 ? (
                        <p className="text-gray-500 text-sm">
                            No campaigns found.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 text-left text-sm text-gray-600">
                                        <th className="px-4 py-3">Name</th>
                                        <th className="px-4 py-3">Start Date</th>
                                        <th className="px-4 py-3">End Date</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaigns.map((campaign) => (
                                        <tr
                                            key={campaign._id}
                                            className="border-t hover:bg-gray-50 transition"
                                        >
                                            <td className="px-4 py-3 font-medium">
                                                {campaign.camp_name}
                                            </td>

                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                {new Date(campaign.camp_start_date).toLocaleDateString()}
                                            </td>

                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                {new Date(campaign.camp_end_date).toLocaleDateString()}
                                            </td>

                                            <td className="px-4 py-3">
                                                <span className={`px-3 py-1 text-xs rounded-full 
                                    ${campaign.camp_status === "active"
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-red-100 text-red-700"}`}>
                                                    {campaign.camp_status}
                                                </span>
                                            </td>

                                            <td className="px-4 py-3">
                                                <div className="flex justify-center gap-3">
                                                    <button
                                                        onClick={() => handleEdit(campaign)}
                                                        className="p-2 rounded-lg hover:bg-blue-100 text-blue-600 transition"
                                                    >
                                                        <Pencil size={18} />
                                                    </button>

                                                    <button
                                                        onClick={() => setDeleteId(campaign.camp_id)}
                                                        className="p-2 rounded-lg hover:bg-red-100 text-red-600 transition"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
            {deleteId && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1111]">
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 animate-fadeIn">

                        <h3 className="text-lg font-semibold mb-2">
                            Delete Campaign
                        </h3>

                        <p className="text-sm text-gray-600 mb-6">
                            Are you sure you want to delete this campaign?
                            This action cannot be undone.
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteId(null)}
                                className="px-4 py-2 rounded-lg border hover:bg-gray-100"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleDelete}
                                disabled={deleteLoading}
                                className="px-4 py-2 rounded-lg bg-red-600 text-white 
                               hover:bg-red-700 flex items-center gap-2 
                               disabled:opacity-70"
                            >
                                {deleteLoading ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        Deleting...
                                    </>
                                ) : (
                                    "Delete"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampaignPage;